<!--
 * @Description: 首页客户区域分布
 * @Author: mdb
 * @Date: 2023-04-11 13:41:37
 * @LastEditTime: 2023-04-11 14:49:13
 * @LastEditors: mdb
-->
<template>
  <div class="home-echarts-wrap" :style="{ width, height }">
    <div class="home-echarts-header">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import type { Ref, PropType } from 'vue'
interface ItemType {
  id: string
  name: string
  num: number
}
interface RegionDistributionEchartProps {
  regionVos: ItemType[]
  [key: string]: any
}

const props = defineProps({
  title: {
    type: String as PropType<string>,
    required: true
  },
  value: {
    type: Object as PropType<RegionDistributionEchartProps>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  spinning: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

const showECharts = ref(false)
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemType[]) => {
  const xAxisData: string[] = data.map((item) => item.name)
  const seriesData: number[] = data.map((item) => item.num)
  setOptions({
    tooltip: {
      trigger: 'axis'
    },
    grid: {
      left: '80',
      right: '50'
    },
    calculable: false,
    xAxis: [
      {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          // 倾斜的程度
          formatter: function (value: any) {
            if (value.length > 7) {
              return `${value.slice(0, 7)}...`
            }
            return value
          }
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: false,
          lineStyle: {
            type: 'dashed'
          }
        },
        axisLabel: {
          // 倾斜的程度
          margin: 2,
          formatter: function (value: any) {
            if (value >= 10000 && value < 100000000) {
              value = value / 10000 + '万'
            } else if (value >= 100000000 && value < 1000000000000) {
              value = value / 100000000 + '亿'
            } else if (value >= 1000000000000) {
              value = value / 1000000000000 + '兆'
            }
            return value
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      }
    ],
    series: [
      {
        type: 'bar',
        name: props.title,
        itemStyle: { color: '#3F97FF' },
        data: seriesData
      }
    ]
  })
}

watch(
  () => props.value,
  (newValue) => {
    if (newValue && newValue.regionVos && newValue.regionVos.length > 0) {
      showECharts.value = true
      setTimeout(() => initChart(newValue.regionVos))
    } else {
      showECharts.value = false
      setTimeout(() => initChart([]))
    }
  },
  {
    deep: true,
    immediate: true
  }
)
</script>
<script lang="ts">
export default { name: 'RegionDistributionChart' }
</script>

<style scoped></style>
